import { AnimatedPageHeader } from '@components/index'
import { Table } from '@components/table'
import { PageRead } from '@components/table/types'
import { useInviteLandlords } from '@services/users/mutations'
import { useClients } from '@services/users/queries/useClients'
import { Mail } from 'lucide-react'
import { Key, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UserList } from 'types/api'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useConfirmLandlordInvite } from '../hooks/useConfirmLandlordInvite'
import { LandlordAccountStatus } from './LandlordAccountStatus'

export function LandlordsTable() {
  const query = useClients({ params: { roles: 'LANDLORD' } })
  const { t } = useTranslation('users')
  const { mutate: inviteLandlords, isPending } = useInviteLandlords()
  const { confirmInvite, dialog } = useConfirmLandlordInvite()
  const [selected, setSelected] = useState<Key[]>([])
  // Table keeps its own selection; remounting it clears the checkboxes.
  const [tableKey, setTableKey] = useState(0)

  async function handleBulkInvite() {
    if (!(await confirmInvite(selected.length))) return
    inviteLandlords(
      { user_ids: selected.map(String) },
      {
        onSuccess: () => {
          setSelected([])
          setTableKey((k) => k + 1)
        }
      }
    )
  }

  const columns = [
    {
      title: t('form.labels.name'),
      dataIndex: 'first_name',
      sorter: true,
      render: (_: unknown, record: UserList) => {
        const name = [record.first_name, record.last_name]
          .filter(Boolean)
          .join(' ')
        return <span className="font-medium text-ink">{name || '—'}</span>
      }
    },
    {
      title: t('form.labels.email'),
      dataIndex: 'email',
      sorter: true,
      render: (value: string) => (
        <span className="text-ink/80">{value || '—'}</span>
      )
    },
    {
      title: t('form.labels.phone'),
      dataIndex: 'phone',
      sorter: true,
      render: (value: string) => (
        <span className="text-ink/80">{value || '—'}</span>
      )
    },
    {
      title: t('landlordStatus.column'),
      dataIndex: 'landlord_inactive',
      sorter: true,
      render: (inactive?: boolean) => (
        <span
          className={cn(
            'inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold',
            inactive
              ? 'bg-ink/[0.07] text-ink/60'
              : 'bg-success-soft text-success'
          )}
        >
          {t(inactive ? 'landlordStatus.inactive' : 'landlordStatus.active')}
        </span>
      )
    },
    {
      title: t('landlordColumns.flats'),
      dataIndex: 'flats_count',
      sorter: true,
      render: (value?: number) => (
        <span className={value ? 'text-ink' : 'text-muted-foreground'}>
          {value ?? 0}
        </span>
      )
    },
    {
      title: t('landlordColumns.activeFlats'),
      dataIndex: 'active_flats_count',
      sorter: true,
      render: (value?: number) => (
        <span
          className={
            value ? 'font-semibold text-success' : 'text-muted-foreground'
          }
        >
          {value ?? 0}
        </span>
      )
    },
    {
      title: t('landlordInvite.column'),
      dataIndex: 'last_login',
      render: (_: unknown, record: UserList) => (
        <LandlordAccountStatus landlord={record} />
      )
    },
    {
      title: t('form.labels.dateJoined'),
      dataIndex: 'date_joined',
      sorter: true,
      render: (value: string) => (
        <span className="text-sm text-muted-foreground">
          {value ? new Date(value).toLocaleDateString('sk-SK') : '—'}
        </span>
      )
    }
  ]

  return (
    <div className="space-y-6">
      <AnimatedPageHeader title={t('landlords')} />
      <Table<PageRead<UserList>>
        key={tableKey}
        query={query}
        columns={columns}
        id="landlords"
        hasCreateButton
        hasRowSelection
        // Landlords who already logged in have a password, and inactive ones
        // are not invited at all.
        rowSelection={{
          getCheckboxProps: (record) => ({
            disabled: !!record.last_login || !!record.landlord_inactive
          })
        }}
        onRowCheck={(keys) => setSelected(keys)}
        actionBar={
          selected.length > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-ink">
                {t('landlordInvite.selected', { count: selected.length })}
              </span>
              <Button size="sm" onClick={handleBulkInvite} disabled={isPending}>
                <Mail className="h-4 w-4" />
                {t('landlordInvite.bulkAction', { count: selected.length })}
              </Button>
            </div>
          )
        }
      />
      {dialog}
    </div>
  )
}
